import {
  AIFilterProvider,
  getAIFilterProviderLabel,
} from "@/interfaces/ai-filter.interface";
import aiFilterApi from "@/api/ai-filter";

type SelectOption<T extends string = string> = { value: T; label: string };
type FieldOption<T extends string = string> = {
  key: T;
  label: string;
  value: T;
};

interface GeminiModelApiItem {
  name?: string;
  description?: string;
}

interface GeminiModelsApiResponse {
  models?: GeminiModelApiItem[];
}

interface OpenAIModelApiItem {
  id?: string;
}

interface OpenAIModelsApiResponse {
  data?: OpenAIModelApiItem[];
}

const toFieldOptions = <T extends string>(
  options: readonly SelectOption<T>[],
): FieldOption<T>[] =>
  options.map((item) => ({
    key: item.value,
    label: item.label,
    value: item.value,
  }));

const toSimpleOptions = <T extends string>(
  values: readonly T[],
): SelectOption<T>[] => values.map((value) => ({ value, label: value }));

export const GOOGLE_TYPE_OPTIONS = [
  // { value: "image_generation", label: "Image Generation" },
  { value: "image_editing", label: "Image Editing" },
] as const satisfies readonly SelectOption[];

export const CUTOUT_TYPE_OPTIONS = [
  { value: "background_removal", label: "Background Removal" },
  { value: "face_cutout", label: "Face Cutout" },
] as const satisfies readonly SelectOption[];

export const BUILT_IN_PROCESSING_TYPE_OPTIONS = [
  { value: "pencil_sketch", label: "Pencil Sketch" },
] as const satisfies readonly SelectOption[];

export type GoogleType = (typeof GOOGLE_TYPE_OPTIONS)[number]["value"];
export type CutoutType = (typeof CUTOUT_TYPE_OPTIONS)[number]["value"];
export type BuiltInProcessingType =
  (typeof BUILT_IN_PROCESSING_TYPE_OPTIONS)[number]["value"];
export type AIFilterType = GoogleType | CutoutType | BuiltInProcessingType;
type TypeOption = SelectOption<AIFilterType>;

export const CUTOUT_OUTPUT_FORMAT_OPTIONS = [
  { value: "png", label: "PNG" },
  { value: "jpg", label: "JPG" },
  { value: "webp", label: "WEBP" },
] as const satisfies readonly SelectOption[];

export const BUILT_IN_OUTPUT_FORMAT_OPTIONS = [
  { value: "png", label: "PNG" },
  { value: "jpeg", label: "JPEG" },
  { value: "webp", label: "WEBP" },
] as const satisfies readonly SelectOption[];

export const POST_PROCESSING_TYPE_NULL_VALUE = "null";

export const POST_PROCESSING_TYPE_OPTIONS = [
  { value: POST_PROCESSING_TYPE_NULL_VALUE, label: "None" },
  // { value: "pencil_sketch", label: "Pencil Sketch" },
  { value: "white_background_removal", label: "White Background Removal" },
  { value: "color_removal_and_fill", label: "Color Removal and Fill" },
] as const satisfies readonly SelectOption[];

export type PostProcessingType =
  | (typeof POST_PROCESSING_TYPE_OPTIONS)[number]["value"]
  | null;

export type PostProcessingConfig = {
  removeColor: string;
  fillColor: string | null;
} | null;

export const POST_PROCESSING_TYPE_FIELD_OPTIONS = toFieldOptions([
  ...POST_PROCESSING_TYPE_OPTIONS,
]);

export type GoogleConfigForm = {
  model: string;
  prompt: string;
  aspectRatio: string | null;
  imageSize: string | null;
  postProcessingType: PostProcessingType;
  postProcessingConfig: PostProcessingConfig;
};

export type VertexConfigForm = {
  model: string;
  prompt: string;
  aspectRatio: string | null;
  imageSize: string | null;
  postProcessingType: PostProcessingType;
  postProcessingConfig: PostProcessingConfig;
};

export type OpenAIConfigForm = {
  model: string;
  prompt: string;
  size: string | null;
  postProcessingType: PostProcessingType;
  postProcessingConfig: PostProcessingConfig;
};

export type CutoutConfigForm = {
  crop: boolean;
  outputFormat: (typeof CUTOUT_OUTPUT_FORMAT_OPTIONS)[number]["value"];
  postProcessingType: PostProcessingType;
  postProcessingConfig: PostProcessingConfig;
};

export type BuiltInProcessingConfigForm = {
  model: string;
  aspectRatio: string | null;
  imageSize: string | null;
  postProcessingType: PostProcessingType;
  postProcessingConfig: PostProcessingConfig;
};

const PROVIDER_ORDER: AIFilterProvider[] = [
  "google",
  "openai",
  "cutout.pro",
  "built_in_processing",
  "vertex",
];

export const PROVIDER_SELECT_OPTIONS: SelectOption<AIFilterProvider>[] =
  PROVIDER_ORDER.map((provider) => ({
    value: provider,
    label: getAIFilterProviderLabel(provider),
  }));
export const PROVIDER_FIELD_OPTIONS = toFieldOptions(PROVIDER_SELECT_OPTIONS);
export const getProviderLabel = (provider?: string) =>
  getAIFilterProviderLabel(provider);

export const GOOGLE_MODEL_FIELD_OPTIONS: FieldOption<string>[] = [];
export const OPENAI_MODEL_FIELD_OPTIONS: FieldOption<string>[] = [];
export const GOOGLE_DEFAULT_MODEL_VALUE = "";
const GEMINI_IMAGE_MODEL_KEYWORDS = ["image", "imagen", "nano-banana"];
const OPENAI_IMAGE_MODEL_KEYWORDS = ["image"];
export const GOOGLE_MODEL_OPTIONS = [
  {
    value: "gemini-2.5-flash-image",
    label: "Nano Banana (Gemini 2.5 Flash Image)",
  },
  {
    value: "gemini-3-pro-image-preview",
    label: "Nano Banana Pro (Gemini 3 Pro Image Preview)",
  },
  {
    value: "gemini-3.1-flash-image-preview",
    label: "Nano Banana 2 (Gemini 3.1 Flash Image Preview)",
  },
] as const satisfies readonly SelectOption[];

const GOOGLE_MODEL_VALUES = GOOGLE_MODEL_OPTIONS.map((item) => item.value);
const GOOGLE_MODEL_SET = new Set<string>(GOOGLE_MODEL_VALUES);
const GOOGLE_IMAGE_SIZE_SUPPORTED_MODELS = new Set<string>([
  "gemini-3-pro-image-preview",
  "gemini-3.1-flash-image-preview",
]);
const GOOGLE_TYPE_VALUES = GOOGLE_TYPE_OPTIONS.map((item) => item.value);
const CUTOUT_TYPE_VALUES = CUTOUT_TYPE_OPTIONS.map((item) => item.value);
const BUILT_IN_PROCESSING_TYPE_VALUES = BUILT_IN_PROCESSING_TYPE_OPTIONS.map(
  (item) => item.value,
);

export const GOOGLE_ASPECT_RATIO_OPTIONS = [
  "1:1",
  "1:4",
  "1:8",
  "2:3",
  "3:2",
  "3:4",
  "4:1",
  "4:3",
  "4:5",
  "5:4",
  "8:1",
  "9:16",
  "16:9",
  "21:9",
] as const;

export const GOOGLE_IMAGE_SIZE_OPTIONS = ["0.5K", "1K", "2K", "4K"] as const;
export const OPENAI_SIZE_OPTIONS = [
  "1024x1024",
  "1024x1536",
  "1536x1024",
] as const;
const GOOGLE_ASPECT_RATIO_SET = new Set<string>(GOOGLE_ASPECT_RATIO_OPTIONS);
const GOOGLE_IMAGE_SIZE_SET = new Set<string>(GOOGLE_IMAGE_SIZE_OPTIONS);
const OPENAI_SIZE_SET = new Set<string>(OPENAI_SIZE_OPTIONS);

export const ASPECT_RATIO_AUTO_VALUE = "null";
export const IMAGE_SIZE_AUTO_VALUE = "null";

export const GOOGLE_ASPECT_RATIO_FIELD_OPTIONS: FieldOption<string>[] = [
  { key: "auto", label: "Auto", value: ASPECT_RATIO_AUTO_VALUE },
  ...toFieldOptions(toSimpleOptions(GOOGLE_ASPECT_RATIO_OPTIONS)),
];

export const GOOGLE_IMAGE_SIZE_FIELD_OPTIONS: FieldOption<string>[] = [
  { key: "auto", label: "Auto", value: IMAGE_SIZE_AUTO_VALUE },
  ...toFieldOptions(toSimpleOptions(GOOGLE_IMAGE_SIZE_OPTIONS)),
];

export const OPENAI_SIZE_AUTO_VALUE = "null";
export const OPENAI_SIZE_FIELD_OPTIONS: FieldOption<string>[] = [
  { key: "auto", label: "Auto", value: OPENAI_SIZE_AUTO_VALUE },
  ...toFieldOptions(toSimpleOptions(OPENAI_SIZE_OPTIONS)),
];


export const CUTOUT_CROP_FIELD_OPTIONS: FieldOption<string>[] = [
  { key: "true", label: "Enabled", value: "true" },
  { key: "false", label: "Disabled", value: "false" },
];

export const CUTOUT_OUTPUT_FORMAT_FIELD_OPTIONS = toFieldOptions(
  CUTOUT_OUTPUT_FORMAT_OPTIONS,
);
const CUTOUT_OUTPUT_FORMAT_VALUES = CUTOUT_OUTPUT_FORMAT_OPTIONS.map(
  (item) => item.value,
);

const CUTOUT_OUTPUT_FORMAT_LABEL_MAP = new Map(
  CUTOUT_OUTPUT_FORMAT_OPTIONS.map((item) => [item.value, item.label]),
);
const POST_PROCESSING_TYPE_LABEL_MAP = new Map(
  POST_PROCESSING_TYPE_OPTIONS.map((item) => [item.value, item.label]),
);

const POST_PROCESSING_TYPE_VALUES = POST_PROCESSING_TYPE_OPTIONS.map(
  (o) => o.value,
);

const normalizePostProcessingConfig = (
  postProcessingType: PostProcessingType,
  value: unknown,
): PostProcessingConfig => {
  if (postProcessingType !== "color_removal_and_fill") {
    return null;
  }

  const obj =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  return {
    removeColor:
      typeof obj.removeColor === "string" && obj.removeColor.trim()
        ? obj.removeColor.trim()
        : "#ffffff",
    fillColor:
      obj.fillColor === null
        ? null
        : typeof obj.fillColor === "string" && obj.fillColor.trim()
          ? obj.fillColor.trim()
          : null,
  };
};

const normalizePostProcessingType = (value: unknown): PostProcessingType => {
  const str = String(value ?? "")
    .trim()
    .toLowerCase();
  if (
    POST_PROCESSING_TYPE_VALUES.includes(str as PostProcessingType & string)
  ) {
    return str === POST_PROCESSING_TYPE_NULL_VALUE
      ? null
      : (str as PostProcessingType);
  }
  return null;
};

const GOOGLE_DEFAULT_CONFIG: GoogleConfigForm = {
  model: GOOGLE_DEFAULT_MODEL_VALUE,
  prompt: "",
  aspectRatio: null,
  imageSize: null,
  postProcessingType: null,
  postProcessingConfig: null,
};

const VERTEX_DEFAULT_CONFIG: VertexConfigForm = {
  model: "",
  prompt: "",
  aspectRatio: null,
  imageSize: null,
  postProcessingType: null,
  postProcessingConfig: null,
};

const OPENAI_DEFAULT_CONFIG: OpenAIConfigForm = {
  model: "",
  prompt: "",
  size: null,
  postProcessingType: null,
  postProcessingConfig: null,
};

const CUTOUT_DEFAULT_CONFIG: CutoutConfigForm = {
  crop: true,
  outputFormat: CUTOUT_OUTPUT_FORMAT_OPTIONS[0].value,
  postProcessingType: null,
  postProcessingConfig: null,
};

const BUILT_IN_PROCESSING_DEFAULT_CONFIG: BuiltInProcessingConfigForm = {
  model: "",
  aspectRatio: null,
  imageSize: null,
  postProcessingType: null,
  postProcessingConfig: null,
};

const getAsString = (value: unknown, fallback: string) => {
  if (typeof value !== "string") {
    return fallback;
  }
  const trimmedValue = value.trim();
  return trimmedValue || fallback;
};

const normalizeOutputFormatValue = <T extends string>(
  value: unknown,
  allowedValues: readonly T[],
  fallback: T,
  aliasMap: Record<string, T> = {},
): T => {
  const normalizedValue = getAsString(value, fallback).toLowerCase();
  const mappedValue = aliasMap[normalizedValue] || (normalizedValue as T);
  return allowedValues.includes(mappedValue) ? mappedValue : fallback;
};

const normalizeCutoutOutputFormat = (
  value: unknown,
): CutoutConfigForm["outputFormat"] =>
  normalizeOutputFormatValue(
    value,
    CUTOUT_OUTPUT_FORMAT_VALUES,
    CUTOUT_DEFAULT_CONFIG.outputFormat,
    { jpeg: "jpg" },
  );

export const getCutoutOutputFormatLabel = (value?: string) =>
  CUTOUT_OUTPUT_FORMAT_LABEL_MAP.get(normalizeCutoutOutputFormat(value)) || "-";

export const getPostProcessingTypeLabel = (value?: string | null) =>
  POST_PROCESSING_TYPE_LABEL_MAP.get(
    normalizePostProcessingType(value) ?? POST_PROCESSING_TYPE_NULL_VALUE,
  ) ?? "None";

export const getDefaultTypeByProvider = (
  provider: AIFilterProvider,
): AIFilterType => {
  if (provider === "google" || provider === "openai" || provider === "vertex") {
    return GOOGLE_TYPE_OPTIONS[0].value;
  }
  if (provider === "cutout.pro") {
    return CUTOUT_TYPE_OPTIONS[0].value;
  }
  return BUILT_IN_PROCESSING_TYPE_OPTIONS[0].value;
};

const LEGACY_TYPE_VALUE_MAP: Record<string, AIFilterType> = {
  // "Image Generation": "image_generation",
  "Image Editing": "image_editing",
  "Background Removal": "background_removal",
  "Remove Background": "background_removal",
  "Face Cutout": "face_cutout",
  "Pencil Sketch": "pencil_sketch",
};

export const normalizeTypeByProvider = (
  provider: AIFilterProvider,
  value?: string,
): AIFilterType => {
  const mappedValue = LEGACY_TYPE_VALUE_MAP[value || ""] || value;
  if (provider === "google" || provider === "openai" || provider === "vertex") {
    return GOOGLE_TYPE_VALUES.includes(mappedValue as GoogleType)
      ? (mappedValue as GoogleType)
      : GOOGLE_TYPE_OPTIONS[0].value;
  }
  if (provider === "cutout.pro") {
    return CUTOUT_TYPE_VALUES.includes(mappedValue as CutoutType)
      ? (mappedValue as CutoutType)
      : CUTOUT_TYPE_OPTIONS[0].value;
  }
  return BUILT_IN_PROCESSING_TYPE_VALUES.includes(
    mappedValue as BuiltInProcessingType,
  )
    ? (mappedValue as BuiltInProcessingType)
    : BUILT_IN_PROCESSING_TYPE_OPTIONS[0].value;
};

export const getTypeOptionsByProvider = (
  provider: AIFilterProvider,
): TypeOption[] => {
  if (provider === "google" || provider === "openai" || provider === "vertex") {
    return [...GOOGLE_TYPE_OPTIONS];
  }
  if (provider === "cutout.pro") {
    return [...CUTOUT_TYPE_OPTIONS];
  }
  return [...BUILT_IN_PROCESSING_TYPE_OPTIONS];
};

export const getTypeFieldOptionsByProvider = (provider: AIFilterProvider) =>
  toFieldOptions(getTypeOptionsByProvider(provider));

export const getTypeLabelByProvider = (
  provider: AIFilterProvider,
  type?: string,
) => {
  const normalizedType = normalizeTypeByProvider(provider, type);
  return (
    getTypeOptionsByProvider(provider).find(
      (item) => item.value === normalizedType,
    )?.label || normalizedType
  );
};

export const supportsGoogleImageSizeConfig = (model?: string) =>
  GOOGLE_IMAGE_SIZE_SUPPORTED_MODELS.has(
    String(model || "")
      .trim()
      .toLowerCase(),
  );

const normalizeGeminiModelValue = (name?: string) => {
  const normalizedName = String(name || "").trim();
  if (!normalizedName) return "";
  return normalizedName.replace(/^models\//i, "").trim();
};

const buildGeminiModelLabel = (model: GeminiModelApiItem) => {
  const description = String(model.description || "").trim();
  const normalizedValue = normalizeGeminiModelValue(model.name).toLowerCase();
  if (!description) {
    return "";
  }
  if (normalizedValue.includes("nano-banana")) {
    return description.toLowerCase().startsWith("nano ")
      ? description
      : `Nano ${description}`;
  }
  return description;
};

const isImageModel = (model: GeminiModelApiItem) => {
  const haystack = [
    String(model.name || "").toLowerCase(),
    String(model.description || "").toLowerCase(),
  ].join(" ");

  return GEMINI_IMAGE_MODEL_KEYWORDS.some((keyword) =>
    haystack.includes(keyword),
  );
};

const toOpenAIModelLabel = (value: string) =>
  value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const isOpenAIImageModel = (model: OpenAIModelApiItem) => {
  const id = String(model.id || "")
    .trim()
    .toLowerCase();
  if (!id) {
    return false;
  }
  return OPENAI_IMAGE_MODEL_KEYWORDS.some((keyword) => id.includes(keyword));
};

export const fetchGoogleImageModelFieldOptions = async (): Promise<
  FieldOption<string>[]
> => {
  try {
    const response = await aiFilterApi.getGeminiModels();
    const data = (response?.data || {}) as GeminiModelsApiResponse;
    const models = Array.isArray(data.models) ? data.models : [];
    const seenValues = new Set<string>();
    const options = models
      .filter(isImageModel)
      .map((model) => {
        const value = normalizeGeminiModelValue(model.name);
        const label = buildGeminiModelLabel(model);
        return {
          key: value,
          value,
          label,
        };
      })
      .filter((option) => {
        if (!option.value || !option.label || seenValues.has(option.value)) {
          return false;
        }
        seenValues.add(option.value);
        return true;
      });

    return options;
  } catch {
    return [];
  }
};

export const fetchOpenAIImageModelFieldOptions = async (): Promise<
  FieldOption<string>[]
> => {
  try {
    const response = await aiFilterApi.getOpenAIModels();
    const data = (response?.data || {}) as OpenAIModelsApiResponse;
    const models = Array.isArray(data.data) ? data.data : [];
    const seenValues = new Set<string>();
    const options = models
      .filter(isOpenAIImageModel)
      .map((model) => {
        const value = String(model.id || "").trim();
        return {
          key: value,
          value,
          label: toOpenAIModelLabel(value),
        };
      })
      .filter((option) => {
        if (!option.value || seenValues.has(option.value)) {
          return false;
        }
        seenValues.add(option.value);
        return true;
      });

    return options;
  } catch {
    return [];
  }
};

export const getDefaultGoogleConfig = (
  config?: Record<string, unknown>,
): GoogleConfigForm => {
  if (!config) {
    return { ...GOOGLE_DEFAULT_CONFIG };
  }
  const modelValue = getAsString(config.model, GOOGLE_DEFAULT_CONFIG.model);
  const rawAspectRatioValue = config.aspectRatio;
  const rawImageSizeValue = config.imageSize;

  let normalizedAspectRatioValue: string | null =
    GOOGLE_DEFAULT_CONFIG.aspectRatio;
  if (rawAspectRatioValue === null) {
    normalizedAspectRatioValue = null;
  } else if (typeof rawAspectRatioValue === "string") {
    const trimmed = rawAspectRatioValue.trim();
    if (trimmed && GOOGLE_ASPECT_RATIO_SET.has(trimmed)) {
      normalizedAspectRatioValue = trimmed;
    }
  }

  let normalizedImageSizeValue: string | null = GOOGLE_DEFAULT_CONFIG.imageSize;
  if (rawImageSizeValue === null) {
    normalizedImageSizeValue = null;
  } else if (typeof rawImageSizeValue === "string") {
    const trimmed = rawImageSizeValue.trim().toUpperCase();
    if (trimmed && GOOGLE_IMAGE_SIZE_SET.has(trimmed)) {
      normalizedImageSizeValue = trimmed;
    }
  }

  const postProcessingType = normalizePostProcessingType(
    config.postProcessingType,
  );
  return {
    model: modelValue || GOOGLE_DEFAULT_CONFIG.model,
    prompt: getAsString(config.prompt, GOOGLE_DEFAULT_CONFIG.prompt),
    aspectRatio: normalizedAspectRatioValue,
    imageSize: normalizedImageSizeValue,
    postProcessingType,
    postProcessingConfig: normalizePostProcessingConfig(
      postProcessingType,
      config.postProcessingConfig,
    ),
  };
};

export const getDefaultVertexConfig = (
  config?: Record<string, unknown>,
): VertexConfigForm => {
  if (!config) {
    return { ...VERTEX_DEFAULT_CONFIG };
  }
  const modelValue = getAsString(config.model, VERTEX_DEFAULT_CONFIG.model);
  const rawAspectRatioValue = config.aspectRatio;
  const rawImageSizeValue = config.imageSize;

  let normalizedAspectRatioValue: string | null =
    VERTEX_DEFAULT_CONFIG.aspectRatio;
  if (rawAspectRatioValue === null) {
    normalizedAspectRatioValue = null;
  } else if (typeof rawAspectRatioValue === "string") {
    const trimmed = rawAspectRatioValue.trim();
    if (trimmed && GOOGLE_ASPECT_RATIO_SET.has(trimmed)) {
      normalizedAspectRatioValue = trimmed;
    }
  }

  let normalizedImageSizeValue: string | null = VERTEX_DEFAULT_CONFIG.imageSize;
  if (rawImageSizeValue === null) {
    normalizedImageSizeValue = null;
  } else if (typeof rawImageSizeValue === "string") {
    const trimmed = rawImageSizeValue.trim().toUpperCase();
    if (trimmed && GOOGLE_IMAGE_SIZE_SET.has(trimmed)) {
      normalizedImageSizeValue = trimmed;
    }
  }

  const postProcessingType = normalizePostProcessingType(
    config.postProcessingType,
  );
  return {
    model: modelValue || VERTEX_DEFAULT_CONFIG.model,
    prompt: getAsString(config.prompt, VERTEX_DEFAULT_CONFIG.prompt),
    aspectRatio: normalizedAspectRatioValue,
    imageSize: normalizedImageSizeValue,
    postProcessingType,
    postProcessingConfig: normalizePostProcessingConfig(
      postProcessingType,
      config.postProcessingConfig,
    ),
  };
};

export const getDefaultOpenAIConfig = (
  config?: Record<string, unknown>,
): OpenAIConfigForm => {
  if (!config) {
    return { ...OPENAI_DEFAULT_CONFIG };
  }

  const rawSize = config.size;
  let normalizedSizeValue: string | null = null;
  if (typeof rawSize === "string") {
    const trimmed = rawSize.trim();
    if (trimmed && trimmed !== "auto" && OPENAI_SIZE_SET.has(trimmed)) {
      normalizedSizeValue = trimmed;
    }
  }

  const postProcessingType = normalizePostProcessingType(
    config.postProcessingType,
  );
  return {
    model: getAsString(config.model, OPENAI_DEFAULT_CONFIG.model),
    prompt: getAsString(config.prompt, OPENAI_DEFAULT_CONFIG.prompt),
    size: normalizedSizeValue,
    postProcessingType,
    postProcessingConfig: normalizePostProcessingConfig(
      postProcessingType,
      config.postProcessingConfig,
    ),
  };
};

export const getDefaultCutoutConfig = (
  config?: Record<string, unknown>,
): CutoutConfigForm => {
  if (!config) {
    return { ...CUTOUT_DEFAULT_CONFIG };
  }
  const postProcessingType = normalizePostProcessingType(
    config.postProcessingType,
  );
  return {
    crop:
      typeof config.crop === "boolean"
        ? config.crop
        : CUTOUT_DEFAULT_CONFIG.crop,
    outputFormat: normalizeCutoutOutputFormat(config.outputFormat),
    postProcessingType,
    postProcessingConfig: normalizePostProcessingConfig(
      postProcessingType,
      config.postProcessingConfig,
    ),
  };
};

export const getDefaultBuiltInProcessingConfig = (
  config?: Record<string, unknown>,
): BuiltInProcessingConfigForm => {
  if (!config) {
    return { ...BUILT_IN_PROCESSING_DEFAULT_CONFIG };
  }

  const modelValue = getAsString(
    config.model,
    BUILT_IN_PROCESSING_DEFAULT_CONFIG.model,
  );
  const rawAspectRatioValue = config.aspectRatio;
  const rawImageSizeValue = config.imageSize;

  let normalizedAspectRatioValue: string | null =
    BUILT_IN_PROCESSING_DEFAULT_CONFIG.aspectRatio;
  if (rawAspectRatioValue === null) {
    normalizedAspectRatioValue = null;
  } else if (typeof rawAspectRatioValue === "string") {
    const trimmed = rawAspectRatioValue.trim();
    if (trimmed && GOOGLE_ASPECT_RATIO_SET.has(trimmed)) {
      normalizedAspectRatioValue = trimmed;
    }
  }

  let normalizedImageSizeValue: string | null =
    BUILT_IN_PROCESSING_DEFAULT_CONFIG.imageSize;
  if (rawImageSizeValue === null) {
    normalizedImageSizeValue = null;
  } else if (typeof rawImageSizeValue === "string") {
    const trimmed = rawImageSizeValue.trim().toUpperCase();
    if (trimmed && GOOGLE_IMAGE_SIZE_SET.has(trimmed)) {
      normalizedImageSizeValue = trimmed;
    }
  }

  const postProcessingType = normalizePostProcessingType(
    config.postProcessingType,
  );
  return {
    model: modelValue || BUILT_IN_PROCESSING_DEFAULT_CONFIG.model,
    aspectRatio: normalizedAspectRatioValue,
    imageSize: normalizedImageSizeValue,
    postProcessingType,
    postProcessingConfig: normalizePostProcessingConfig(
      postProcessingType,
      config.postProcessingConfig,
    ),
  };
};

export const buildConfigByProvider = (
  provider: AIFilterProvider,
  data: {
    googleConfig: GoogleConfigForm;
    openAIConfig: OpenAIConfigForm;
    cutoutConfig: CutoutConfigForm;
    builtInProcessingConfig: BuiltInProcessingConfigForm;
    vertexConfig: VertexConfigForm;
  },
) => {
  if (provider === "google") {
    const model = data.googleConfig.model.trim();
    const nextConfig: Record<string, string | null | PostProcessingConfig> = {
      model,
      prompt: data.googleConfig.prompt.trim(),
      aspectRatio: data.googleConfig.aspectRatio,
      postProcessingType: data.googleConfig.postProcessingType,
      postProcessingConfig: data.googleConfig.postProcessingConfig,
    };
    if (supportsGoogleImageSizeConfig(model)) {
      nextConfig.imageSize = data.googleConfig.imageSize;
    }
    return {
      ...nextConfig,
    };
  }
  if (provider === "vertex") {
    const model = data.vertexConfig.model.trim();
    const nextConfig: Record<string, string | null | PostProcessingConfig> = {
      model,
      prompt: data.vertexConfig.prompt.trim(),
      aspectRatio: data.vertexConfig.aspectRatio,
      postProcessingType: data.vertexConfig.postProcessingType,
      postProcessingConfig: data.vertexConfig.postProcessingConfig,
    };
    if (supportsGoogleImageSizeConfig(model)) {
      nextConfig.imageSize = data.vertexConfig.imageSize;
    }
    return nextConfig;
  }
  if (provider === "openai") {
    return {
      model: data.openAIConfig.model.trim(),
      prompt: data.openAIConfig.prompt.trim(),
      size: data.openAIConfig.size,
      postProcessingType: data.openAIConfig.postProcessingType,
      postProcessingConfig: data.openAIConfig.postProcessingConfig,
    };
  }
  if (provider === "cutout.pro") {
    return {
      crop: data.cutoutConfig.crop,
      outputFormat: normalizeCutoutOutputFormat(data.cutoutConfig.outputFormat),
      postProcessingType: data.cutoutConfig.postProcessingType,
      postProcessingConfig: data.cutoutConfig.postProcessingConfig,
    };
  }
  const builtInModel = data.builtInProcessingConfig.model.trim();
  const builtInConfig: Record<
    string,
    string | boolean | null | PostProcessingConfig
  > = {
    model: builtInModel,
    aspectRatio: data.builtInProcessingConfig.aspectRatio,
    postProcessingType: data.builtInProcessingConfig.postProcessingType,
    postProcessingConfig: data.builtInProcessingConfig.postProcessingConfig,
  };
  if (supportsGoogleImageSizeConfig(builtInModel)) {
    builtInConfig.imageSize = data.builtInProcessingConfig.imageSize;
  }
  return builtInConfig;
};
